import React, { useContext, useState, useEffect } from "react"
import { useMutation } from "@apollo/client"
import { startCase, isEmpty } from "lodash"
import classNames from "classnames"
import { parse } from "query-string"
import Label from "../../../profile/Label"
import { Input } from "../../../components/inputs"
import Select from "../../../components/select/Select"
import { SelectedPlansContext } from "../../SelectedPlansContext"
import { CREATE_SUBSCRIPTION } from "../../queries"
import { ArrowDownIcon, ArrowUpIcon } from "../../images/icons"
import SubscriptionModel from "../../SubscriptionModel"
import SignInForm from "../SignInForm"
import styles from "../../styles.module.scss"
import { useLocation } from "react-router-dom"

const SubscriptionForm = ({ userType }) => {
  const location = useLocation()
  const urlParams = parse(location.search)

  const {
    selectedPlans,
    setSelectedPlans,
    newClientDetails: defaultNewClientDetails,
    setNewClientDetails: setDefaultNewClientDetails,
    toggleSubscriptionModal,
    addWorkPassPlan,
    addPayrollManagedServicesPlan,
    notifyMessage,
  } = useContext(SelectedPlansContext)

  const [viewMoreTrigger, setViewMoreTrigger] = useState({})
  const [newClientDetails, setNewClientDetails] = useState(
    defaultNewClientDetails
  )
  const [showSignInForm, setShowSignInForm] = useState(false)
  const [errors, setErrors] = useState({})

  const [createSubscription, { loading: creating }] =
    useMutation(CREATE_SUBSCRIPTION)

  const subscription = new SubscriptionModel(
    newClientDetails,
    selectedPlans,
    userType
  )

  useEffect(() => {
    selectedPlans.map(({ planType }) => {
      setViewMoreTrigger({
        ...viewMoreTrigger,
        [planType]: false,
      })
    })
  }, [])

  const RateLabel = ({ planType, rate, rateType, determinant, otherRates }) => {
    if (planType === "Payroll Managed Services") {
      return (
        <div className={styles.rate}>
          <h3>Custom</h3>
          <p>per employee per month</p>
        </div>
      )
    } else if (planType === "Talent Search") {
      const placementFeeItem = otherRates.placementFeeItems[0]

      return (
        <div className={styles.rate}>
          <h3>{placementFeeItem.rate2}%</h3>
          <p>{startCase(placementFeeItem.determinant)}</p>
        </div>
      )
    } else if (planType === "Work Pass") {
      return (
        <div className={styles.rate}>
          <h3>${rate}</h3>
          <p>
            {determinant}
            {/*<Tooltip id="workPassSubscriptionDeterminant" width={450}>*/}
            {/*  <span>*/}
            {/*    Rate decreases with increasing work pass applications:*/}
            {/*  </span>*/}
            {/*  <ul>*/}
            {/*    <li>1st - 3rd: $600 per application</li>*/}
            {/*    <li>4th - 6th: $500 per application</li>*/}
            {/*    <li>> 7th: $400 per application </li>*/}
            {/*  </ul>*/}
            {/*</Tooltip>*/}
          </p>
        </div>
      )
    }
  }

  const AddPlansLabel = () => {
    const selectedWorkPassPlan = selectedPlans.filter(
      (plan) => plan.planType === "Work Pass"
    )[0]
    const selectedPayrollPlan = selectedPlans.filter(
      (plan) => plan.planType === "Payroll Managed Services"
    )[0]

    if (!selectedWorkPassPlan && !selectedPayrollPlan) {
      return (
        <p>
          Would you like to include in a Payroll Managed Services Plan or Work
          Pass Plan as well? Add a{" "}
          <button
            className={styles.link}
            onClick={() => {
              addWorkPassPlan()
              setDefaultNewClientDetails({ ...newClientDetails })
            }}
          >
            Work Pass Plan
          </button>{" "}
          or{" "}
          <button
            className={styles.link}
            onClick={() => {
              addPayrollManagedServicesPlan()
              setDefaultNewClientDetails({ ...newClientDetails })
            }}
          >
            Payroll Managed Services Plan
          </button>
        </p>
      )
    } else if (!selectedWorkPassPlan) {
      return (
        <p>
          Would you like to include in a Work Pass Plan as well? Add a{" "}
          <button
            className={styles.link}
            onClick={() => {
              addWorkPassPlan()
              setDefaultNewClientDetails({ ...newClientDetails })
            }}
          >
            Work Pass Plan
          </button>
        </p>
      )
    } else if (!selectedPayrollPlan) {
      return (
        <p>
          Would you like to include in a Payroll Managed Services as well? Add a{" "}
          <button
            className={styles.link}
            onClick={() => {
              addPayrollManagedServicesPlan()
              setDefaultNewClientDetails({ ...newClientDetails })
            }}
          >
            Payroll Managed Services Plan
          </button>
        </p>
      )
    } else {
      return null
    }
  }

  const removePlan = (planType, planName) => {
    setDefaultNewClientDetails({ ...newClientDetails })

    const newSelectedPlans = selectedPlans.filter(
      (plan) => !(plan.planType === planType && plan.planName === planName)
    )
    setSelectedPlans([...newSelectedPlans])

    if (newSelectedPlans.length === 0) toggleSubscriptionModal()
  }

  const handleClientDetailsInputChange = (e) => {
    setNewClientDetails({
      ...newClientDetails,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className={styles["subscription-form"]}>
      <div className={classNames(styles.content, styles[userType])}>
        {userType === "visitor" &&
          (showSignInForm ? (
            <SignInForm setShowSignInForm={setShowSignInForm} />
          ) : (
            <div className={classNames(styles.form, styles["account-form"])}>
              <p style={{ margin: "0 0 1rem 0" }}>
                Don&apos;t have an account with us yet? Create one now.
                <br />
                Already a registered user?{" "}
                <button
                  className={styles.link}
                  onClick={() => setShowSignInForm(true)}
                >
                  Sign In
                </button>{" "}
                now.
              </p>

              <div className={styles.items}>
                <div className={styles.row}>
                  <div className={styles.item}>
                    <Label name="firstName" />
                    <Input
                      name="firstName"
                      value={newClientDetails.firstName}
                      onChange={handleClientDetailsInputChange}
                      error={errors.firstName}
                    />
                  </div>

                  <div className={styles.item}>
                    <Label name="lastName" />
                    <Input
                      name="lastName"
                      value={newClientDetails.lastName}
                      onChange={handleClientDetailsInputChange}
                      error={errors.lastName}
                    />
                  </div>
                </div>

                <div className={styles.item}>
                  <Label name="clientDesignation" text="Designation" />
                  <Input
                    name="clientDesignation"
                    value={newClientDetails.clientDesignation}
                    onChange={handleClientDetailsInputChange}
                    error={errors.clientDesignation}
                  />
                </div>

                <div className={styles.item}>
                  <Label name="email" />
                  <Input
                    name="email"
                    value={newClientDetails.email}
                    onChange={handleClientDetailsInputChange}
                    error={errors.email}
                  />
                </div>

                <div className={styles.item}>
                  <Label name="clientPhoneNumber" text="Phone Number" />
                  <Input
                    type="tel"
                    name="clientPhoneNumber"
                    value={newClientDetails.clientPhoneNumber}
                    onChange={handleClientDetailsInputChange}
                    error={errors.clientPhoneNumber}
                  />
                </div>

                <div className={styles.item}>
                  <Label name="companyName" />
                  <Input
                    name="companyName"
                    value={newClientDetails.companyName}
                    onChange={handleClientDetailsInputChange}
                    error={errors.companyName}
                  />
                </div>

                <div className={styles.row}>
                  <div className={styles.item}>
                    <Label name="companyUen" text="Company UEN" />
                    <Input
                      name="companyUen"
                      value={newClientDetails.companyUen}
                      onChange={handleClientDetailsInputChange}
                      error={errors.companyUen}
                    />
                  </div>

                  <div className={styles.item}>
                    <Label name="companyWebsite" />
                    <Input
                      type="url"
                      name="companyWebsite"
                      value={newClientDetails.companyWebsite}
                      onChange={handleClientDetailsInputChange}
                      error={errors.companyWebsite}
                    />
                  </div>
                </div>

                <div className={styles.item}>
                  <Label name="companyStaffsStrength" text="No. of Employees" />
                  <Select
                    name="companyStaffsStrength"
                    options={[
                      "0 - 10",
                      "11 - 50",
                      "51 - 100",
                      "100 - 500",
                      "> 500",
                    ]}
                    value={newClientDetails.companyStaffsStrength}
                    onChange={handleClientDetailsInputChange}
                    error={errors.companyStaffsStrength}
                    includeDefault
                  />
                </div>
              </div>
            </div>
          ))}

        <div className={styles["plans-form"]}>
          <h2>
            <b>Selected Plans ({selectedPlans.length})</b>
          </h2>

          {selectedPlans.map(
            ({
              planType,
              planName,
              items,
              rate,
              rateType,
              determinant,
              otherRates,
            }) => {
              return (
                <div
                  key={`subscription-${planType}-${planName}`}
                  className={styles.service}
                >
                  <div className={styles.row}>
                    <div className={styles.description}>
                      <h3>
                        {planType === "Work Pass"
                          ? "Work Pass Services"
                          : startCase(planType)}
                      </h3>

                      <div className={styles.details}>
                        {planName} Plan |{" "}
                        <React.Fragment>
                          <button
                            className={classNames(styles.link, styles.dark)}
                            onClick={() => {
                              setViewMoreTrigger({
                                ...viewMoreTrigger,
                                [planType]: !viewMoreTrigger[planType],
                              })
                            }}
                          >
                            {viewMoreTrigger[planType] ? (
                              <React.Fragment>
                                View less
                                <ArrowUpIcon fill="#0f0f0e" />
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                View more
                                <ArrowDownIcon fill="#0f0f0e" />
                              </React.Fragment>
                            )}
                          </button>
                        </React.Fragment>
                        {/*{items && items.length > 0 && (*/}
                        {/*  <React.Fragment>*/}
                        {/*    <button*/}
                        {/*      className={styles.link}*/}
                        {/*      onClick={() => {*/}
                        {/*        setViewMoreTrigger({*/}
                        {/*          ...viewMoreTrigger,*/}
                        {/*          [planType]: !viewMoreTrigger[planType],*/}
                        {/*        })*/}
                        {/*      }}*/}
                        {/*    >*/}
                        {/*      {viewMoreTrigger[planType] ? (*/}
                        {/*        <React.Fragment>*/}
                        {/*          View less <ArrowUpIcon />*/}
                        {/*        </React.Fragment>*/}
                        {/*      ) : (*/}
                        {/*        <React.Fragment>*/}
                        {/*          View more <ArrowDownIcon />*/}
                        {/*        </React.Fragment>*/}
                        {/*      )}*/}
                        {/*    </button>{" "}*/}
                        {/*    |{" "}*/}
                        {/*  </React.Fragment>*/}
                        {/*)}*/}
                      </div>

                      {planType === "Payroll Managed Services" &&
                        planName === "Custom" && (
                          <p className={styles["custom-liner"]}>
                            *Our dedicated consultant will contact you to know
                            more on your business needs.
                          </p>
                        )}
                    </div>

                    <div className={styles.pricing}>
                      <RateLabel
                        planType={planType}
                        rate={rate}
                        rateType={rateType}
                        determinant={determinant}
                        otherRates={otherRates}
                      />
                    </div>
                  </div>

                  {viewMoreTrigger[planType] ? (
                    planType === "Payroll Managed Services" ? (
                      <div
                        className={classNames(
                          styles.row,
                          styles["more-details"]
                        )}
                      >
                        <p>
                          The Payroll Managed Plan is customised to your
                          business and scalable with your growing needs. We will
                          contact you to provide a service quote
                        </p>
                      </div>
                    ) : items && items.length > 0 ? (
                      <div
                        className={classNames(
                          styles.row,
                          styles["more-details"]
                        )}
                      >
                        <p>Plan includes the following:</p>

                        <ul>
                          {items.map(({ label, bold }) => {
                            return <li key={label}>{label}</li>
                          })}
                        </ul>
                      </div>
                    ) : null
                  ) : null}

                  <button
                    className={styles.remove}
                    onClick={() => removePlan(planType, planName)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )
            }
          )}

          <AddPlansLabel />

          <div
            className={classNames(
              styles.actions,
              styles["flex-direction-column"]
            )}
            style={{ margin: "2rem 0 0 0" }}
          >
            <button
              type="button"
              className={classNames(styles.button, styles.dark)}
              disabled={
                creating || selectedPlans.length === 0 || userType === "staff"
              }
              onClick={() => {
                subscription.validateNewSubscription()

                if (isEmpty(subscription.errors)) {
                  createSubscription({
                    variables: {
                      subscription: {
                        selectedPlans: selectedPlans,
                        newClientDetails: newClientDetails,
                        token: urlParams.token,
                      },
                    },
                  }).then(({ data }) => {
                    const message = data.createSubscription.message
                    notifyMessage(message)

                    if (message.type === "success") {
                      setSelectedPlans([])
                      toggleSubscriptionModal()

                      if (userType === "client")
                        window.location.href = "/e/service-plans/"
                    }
                  })
                } else {
                  setErrors(subscription.errors)
                }
              }}
            >
              Proceed With Sign Up
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionForm
